/**
 * Hook for managing infrastructure map state
 *
 * Provides state management for canvas positions, map style settings,
 * layout calculation, and link creation.
 */

import { useState, useCallback } from 'react'
import type {
  MapStyle,
  CanvasViewport,
  CanvasPosition,
  TileSize,
  EndpointDisplayInfo,
  SummaryTileData,
  MapSetResult,
} from '../types/infrastructure-map'
import { useMapSet } from './use-map-set'
import { useCollapseTree } from './infrastructure-map/use-collapse-tree'
import { useDisplayInfo } from './infrastructure-map/use-display-info'
import { useSummaryTiles } from './infrastructure-map/use-summary-tiles'
import { useCollapseActions } from './infrastructure-map/use-collapse-actions'
import { useMapActions } from './infrastructure-map/use-map-actions'
import { useCanvasStates } from './infrastructure-map/use-canvas-states'
import { useMapStyleSettings } from './infrastructure-map/use-map-style-settings'
import { useLayoutCalculation } from './infrastructure-map/use-layout-calculation'
import { useLinkCreation } from './infrastructure-map/use-link-creation'
import type { UseCanvasStatesResult } from './infrastructure-map/use-canvas-states'
import type { UseMapStyleSettingsResult } from './infrastructure-map/use-map-style-settings'
import type { UseLayoutCalculationResult } from './infrastructure-map/use-layout-calculation'
import type { UseLinkCreationResult } from './infrastructure-map/use-link-creation'
import type { PendingExpandConfirmation } from './infrastructure-map/use-collapse-actions'

export { useCanvasStates } from './infrastructure-map/use-canvas-states'
export { useMapStyleSettings } from './infrastructure-map/use-map-style-settings'
export { useLayoutCalculation } from './infrastructure-map/use-layout-calculation'
export { useLinkCreation } from './infrastructure-map/use-link-creation'

export interface UseInfrastructureMapOptions {
  caseId: string | null
  endpointIds: string[]
  linkedEndpointIds: Set<string>
  parentChildMap?: Map<string, string[]>
  externalMapSet?: MapSetResult
}

export interface UseInfrastructureMapResult {
  mapStyle: MapStyle
  viewport: CanvasViewport
  displayInfo: Map<string, EndpointDisplayInfo>
  orphanIds: string[]
  canvasStates: UseCanvasStatesResult
  styleSettings: UseMapStyleSettingsResult
  layout: UseLayoutCalculationResult
  linkCreation: UseLinkCreationResult
  mapSet: MapSetResult
  summaryTiles: Map<string, SummaryTileData>
  pendingExpandConfirmation: PendingExpandConfirmation | null
  setMapStyle: (style: MapStyle) => void
  setViewport: (viewport: Partial<CanvasViewport>) => void
  moveNode: (endpointId: string, position: CanvasPosition) => Promise<void>
  resizeTile: (endpointId: string, size: TileSize) => Promise<void>
  recalculateLayout: (rootNodeId?: string | null) => Promise<void>
  toggleCollapse: (endpointId: string) => Promise<void>
  toggleCollapseRecursive: (endpointId: string) => void
  confirmRecursiveAction: () => Promise<void>
  cancelRecursiveAction: () => void
  getChildrenCount: (endpointId: string) => number
  canCollapse: (endpointId: string) => boolean
}

const DEFAULT_VIEWPORT: CanvasViewport = {
  x: 0,
  y: 0,
  zoom: 1,
  width: 800,
  height: 600,
}

function useViewportState(): {
  viewport: CanvasViewport
  setViewport: (partial: Partial<CanvasViewport>) => void
} {
  const [viewport, setViewportState] = useState<CanvasViewport>(DEFAULT_VIEWPORT)
  const setViewport = useCallback((partial: Partial<CanvasViewport>) => {
    setViewportState((prev) => ({ ...prev, ...partial }))
  }, [])
  return { viewport, setViewport }
}

export function useInfrastructureMap(
  options: UseInfrastructureMapOptions
): UseInfrastructureMapResult {
  const { caseId, endpointIds, linkedEndpointIds, parentChildMap, externalMapSet } = options
  const [mapStyle, setMapStyle] = useState<MapStyle>('tree')
  const { viewport, setViewport } = useViewportState()

  const canvasStates = useCanvasStates(caseId, mapStyle)
  const styleSettings = useMapStyleSettings(caseId, mapStyle)
  const layout = useLayoutCalculation(caseId)
  const linkCreation = useLinkCreation()
  const internalMapSet = useMapSet(externalMapSet ? null : caseId)
  const mapSet = externalMapSet ?? internalMapSet

  const tree = useCollapseTree(parentChildMap, canvasStates.canvasStatesMap)
  const { displayInfo, orphanIds } = useDisplayInfo({
    endpointIds,
    mapSet,
    linkedEndpointIds,
    canvasStatesMap: canvasStates.canvasStatesMap,
    layoutResult: layout.lastResult,
    findCollapsedAncestor: tree.findCollapsedAncestor,
  })
  const summaryTiles = useSummaryTiles({
    displayInfo,
    childrenByParent: tree.childrenByParent,
    getAllDescendants: tree.getAllDescendants,
  })
  const collapseActions = useCollapseActions({
    canvasStates,
    childrenByParent: tree.childrenByParent,
    getAllDescendants: tree.getAllDescendants,
  })
  const mapActions = useMapActions({ canvasStates, styleSettings, layout, mapStyle })

  return {
    mapStyle,
    viewport,
    displayInfo,
    orphanIds,
    canvasStates,
    styleSettings,
    layout,
    linkCreation,
    mapSet,
    summaryTiles,
    pendingExpandConfirmation: collapseActions.pendingExpandConfirmation,
    setMapStyle,
    setViewport,
    moveNode: mapActions.moveNode,
    resizeTile: mapActions.resizeTile,
    recalculateLayout: mapActions.recalculateLayout,
    toggleCollapse: collapseActions.toggleCollapse,
    toggleCollapseRecursive: collapseActions.toggleCollapseRecursive,
    confirmRecursiveAction: collapseActions.confirmRecursiveAction,
    cancelRecursiveAction: collapseActions.cancelRecursiveAction,
    getChildrenCount: collapseActions.getChildrenCount,
    canCollapse: collapseActions.canCollapse,
  }
}
